import { Fragment, React, PropTypes, reduxConnect, useRef, useEffect, useTheme } from '../lib/imports';

import { ErrorContainer, NotificationBar, Spacing, Teaser, SectionTitle, Divider } from '../components';
import { PlayoffHeatStreakBanner } from '../playnow/styled/PlayoffHeatStreakBanner';
import { ChallengeDisclaimer } from '../challenges/Challenges';
import Games from '../games/Games';
import Picks, { Container } from '../picks/Picks';
import Offers from '../offers/Offers';
import CurrentCard from '../challenges/CurrentCard';
import useElementHeight from '../app/hooks/useElementHeight';
import { setNotificationBarToClosed, setElementHeight } from '../ui/actions';
import { getNotificationText } from '../pages/utils';
import { useCdnImage, getAssetSet } from '../app/context/config';
import { useSelector } from 'react-redux';
import { PLAY_NOW_TERMS_AND_CONDITIONS_CLICKED } from '../lib/constants';
import NoChallengeCard from '../challenges/NoChallengeCard';
import { HeatStreakInfoBanner } from '../playnow/styled/HeatStreakInfoBanner';
import Reminders from '../reminders/Reminders';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const PlayNow = ({
  showNotificationBar,
  slotOpenId,
  notificationBarTitle,
  notificationBarText,
  gameListEnabled,
  randomPickCount,
  randomPicksSelected,
  playerPicksEnabled,
  scrollPosition,
  teaserIntroTitle,
  teaserIntroSubTitle,
  showReminders,
  showScoutPicks,
  handleNotificationBarCloseClick,
  handleElementHeight,
  advertisingPromotion,
  playOffTitle,
  seasonTitle,
  seasonText,
  disclaimer,
  playOffText,
  playoffHeatStreakBannerTitle,
  playoffHeatStreakBannerText,
  playerPicksSectionTitle,
  heatStreakInfoBannerImgSrc,
  heatStreakInfoBannerTitle,
  heatStreakInfoBannerText,
  disclaimerTerms,
}) => {
  const { config, leaderboard, strings } = useSelector((state) => state);
  const { language } = config;

  const prevRandomPickCountRef = useRef(0);
  const notificationBarRef = useRef(0);
  const gameListRef = useRef(0);
  const theme = useTheme();
  const notificationBarHeight = useElementHeight(notificationBarRef);
  const gameListHeight = useElementHeight(gameListRef);
  const { isCurrentlyPlayoffs, isCurrentlySeason } = leaderboard;
  const noChallenge = !isCurrentlyPlayoffs && !isCurrentlySeason;
  const gamesMissing = false;
  const playOffCardProps = {
    title: playOffTitle,
    logosSrc: [{ src: `2025_Playoffs_Prize_${language}.png`, width: '100%' }],
    challengeDisclaimer: <ChallengeDisclaimer>{disclaimer}</ChallengeDisclaimer>,
    background: `${theme.colors.darkBg} no-repeat top / 100% 266px url( ${assetSet('challengeCardBackground')})`,
    sponsorText: playOffText,
    analyticsTitle: PLAY_NOW_TERMS_AND_CONDITIONS_CLICKED,
    heatStreak: (
      <PlayoffHeatStreakBanner
        heatStreakBannerImg1Src={assetSet('heatStreakBannerImg1Src')}
        heatStreakBannerImg2Src={assetSet('heatStreakBannerImg2Src')}
        playoffHeatStreakBannerTitle={playoffHeatStreakBannerTitle}
        playoffHeatStreakBannerText={playoffHeatStreakBannerText}
      />
    ),
  };
  const seasonCardProps = {
    title: seasonTitle,
    logosSrc: [{ src: `2025-2026_Season_Prize.png`, width: '100%' }],
    challengeDisclaimer: <ChallengeDisclaimer>{disclaimer}</ChallengeDisclaimer>,
    background: `${theme.colors.darkBg} no-repeat top / 100% url( ${assetSet('challengeCardBackground')})`,
    sponsorText: seasonText,
    analyticsTitle: PLAY_NOW_TERMS_AND_CONDITIONS_CLICKED,
  };

  const errorMessage = strings['notifications/error/playnow/text'];

  useEffect(() => {
    if (!notificationBarHeight || !gameListHeight) {
      return;
    }

    handleElementHeight({
      notificationBar: notificationBarHeight,
      gameList: gameListHeight,
    });
  }, [notificationBarHeight, gameListHeight]);

  useEffect(() => {
    if (!randomPicksSelected || !randomPickCount || prevRandomPickCountRef.current === randomPickCount) {
      return;
    }
    prevRandomPickCountRef.current = randomPickCount;
  }, [slotOpenId, scrollPosition, randomPickCount]);

  if (noChallenge) {
    return <NoChallengeCard />;
  }

  return (
    <>
      {showNotificationBar && (
        <div ref={notificationBarRef}>
          <NotificationBar
            title={notificationBarTitle}
            text={notificationBarText}
            onSelect={handleNotificationBarCloseClick}
          />
        </div>
      )}
      <>
        {gameListEnabled && (
          <>
            {gamesMissing && <ErrorContainer message={errorMessage} />}
            <div ref={gameListRef}>
              <Games />
            </div>
          </>
        )}
        {playerPicksEnabled ? (
          <Picks showScoutPicks={showScoutPicks} showReminders={showReminders} />
        ) : (
          <Container>
            <Spacing sizeBottom='mega'>
              <SectionTitle text={playerPicksSectionTitle} />
            </Spacing>
            <Spacing sizeTop='giga' sizeLeft='giga' sizeRight='giga' sizeBottom='tera'>
              <Teaser src={assetSet(['sticks'])} introTitle={teaserIntroTitle} introSubTitle={teaserIntroSubTitle} />
            </Spacing>
          </Container>
        )}
        {!playerPicksEnabled && <Reminders />}
        <Offers promotions={advertisingPromotion} />
        {!isCurrentlyPlayoffs && (
          <Spacing sizeTop='byte'>
            <CurrentCard {...seasonCardProps} active={isCurrentlySeason} />
          </Spacing>
        )}
        {isCurrentlyPlayoffs && (
          <Spacing sizeTop='byte'>
            <CurrentCard {...playOffCardProps} active={isCurrentlyPlayoffs} />
          </Spacing>
        )}
        <HeatStreakInfoBanner
          imageSrc={heatStreakInfoBannerImgSrc}
          title={heatStreakInfoBannerTitle}
          text={heatStreakInfoBannerText}
          disclaimerTerms={disclaimerTerms}
        />
        <Divider style={{ marginTop: '16px' }} />
      </>
    </>
  );
};

PlayNow.propTypes = {
  showNotificationBar: PropTypes.bool,
  slotOpenId: PropTypes.number,
  notificationBarTitle: PropTypes.string,
  notificationBarText: PropTypes.string,
  gameListEnabled: PropTypes.bool,
  playerPicksEnabled: PropTypes.bool,
  randomPickCount: PropTypes.number,
  randomPicksSelected: PropTypes.bool,
  scrollPosition: PropTypes.number,
  teaserIntroTitle: PropTypes.string,
  showReminders: PropTypes.bool,
  showScoutPicks: PropTypes.bool,
  handleNotificationBarCloseClick: PropTypes.func,
  handleElementHeight: PropTypes.func,
  playerPicksSectionTitle: PropTypes.string,
};

const mapStateToProps = (state) => {
  const { strings, games, picks, ui, config } = state;
  const { allGamesStarted, noGamesStarted, hasGamesToday } = games;
  const {
    fetchError: badPickSubmission,
    picksSentAlready,
    picksLocked,
    randomPickCount,
    randomPicksSelected,
    slotOpenId,
  } = picks;
  const { notificationBarOpen, componentSize, remindersOn, hasFetchError, fetchErrorCount } = ui;
  const { picksScrollPosition } = componentSize;
  const { advertisingPromotion } = config;

  const teaserIntroTitle =
    (hasFetchError && fetchErrorCount >= 3 && strings['global/fetchError/text']) ||
    (!hasGamesToday && strings['notifications/games/noGames/text']) ||
    null;
  const teaserIntroSubTitle = strings['notifications/games/noGames/sub-text'];

  const someGamesInProgress = hasGamesToday && !noGamesStarted && !allGamesStarted;

  let notificationBarText;

  if (noGamesStarted) {
    notificationBarText = getNotificationText(strings, 'welcome');
  } else {
    if (!picksSentAlready && someGamesInProgress) notificationBarText = getNotificationText(strings, 'inProgress');

    if (!picksSentAlready && allGamesStarted) notificationBarText = getNotificationText(strings, 'allInProgress');

    if (!picksSentAlready && !hasGamesToday) notificationBarText = getNotificationText(strings, 'noGames');
  }

  const isSubmitted = picksLocked || picksSentAlready;

  const showNotificationBar = !badPickSubmission && !isSubmitted && notificationBarOpen;

  const gameListEnabled =
    (!hasFetchError && fetchErrorCount >= 3 && picksSentAlready) || (!hasFetchError && hasGamesToday);

  const playerPicksEnabled =
    (!hasFetchError && fetchErrorCount >= 3 && picksSentAlready) ||
    (!hasFetchError && hasGamesToday && allGamesStarted && picksSentAlready) ||
    (!hasFetchError && hasGamesToday && !allGamesStarted);
  const showReminders = !hasFetchError;

  const showScoutPicks = !hasFetchError && !isSubmitted && !picksSentAlready && playerPicksEnabled;

  const scrollPosition = notificationBarOpen
    ? picksScrollPosition.withNotification
    : picksScrollPosition.withoutNotification;

  const allStrings = {
    disclaimer: strings['challenges/footer/disclaimer'],
    playOffTitle: strings['challenges/playoff/title'],
    playOffText: strings['challenges/playoff/text'],
    playoffPrizes: strings['challenges/playoffs/prizes'],
    playoffHeatStreakBannerTitle: strings['challenges/playoffs/heatstreakbanner/title'],
    playoffHeatStreakBannerText: strings['challenges/playoffs/heatstreakbanner/text'],
    seasonTitle: strings['challenges/season/title/playnow'],
    seasonText: strings['challenges/season/text'],
    playerPicksSectionTitle: strings['titles/section/picks/text'],
    heatStreakInfoBannerImgSrc: strings['challenges/heatstreak/banner/img'],
    heatStreakInfoBannerTitle: strings['challenges/heatstreak/banner/title'],
    heatStreakInfoBannerText: strings['challenges/heatstreak/banner/text'],
    disclaimerTerms: strings['heatstreak/gameinfo/disclaimerTerms'],
  };

  return {
    teaserIntroTitle,
    teaserIntroSubTitle,
    slotOpenId,
    randomPickCount,
    randomPicksSelected,
    showNotificationBar,
    gameListEnabled,
    playerPicksEnabled,
    showReminders,
    showScoutPicks,
    scrollPosition,
    remindersOn,
    advertisingPromotion,
    ...notificationBarText,
    ...allStrings,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    handleElementHeight: (height) => dispatch(setElementHeight(height)),
    handleNotificationBarCloseClick: () => dispatch(setNotificationBarToClosed()),
  };
};

export default reduxConnect(mapStateToProps, mapDispatchToProps)(PlayNow);
